import React, { useState, useRef, useEffect } from "react";
import { themeTokens } from "@carvana/showroom-css-theme";
import { cx } from "@linaria/core";
import SliderHeader from "./SliderHeader";
import SliderContext from "./SliderContext";
import {
  valueInRange,
  displayFormatter,
  getValueFromPixels,
  valueIsCloserToSecond
} from "./Slider.operations";
import useSliderGradient from "./useSliderGradient";
import { SliderOuterWrapper } from "./Slider.styles";
import SliderHandle from "./SliderHandle";
const dummyFn = () => {
};
const Slider = ({
  activeColor = "",
  backgroundColor = "",
  className = "",
  debounceDelay = 2e3,
  disabled = false,
  dualRange = false,
  errorMessage = "Please enter a valid value",
  format = "number",
  id,
  initialValues = [0, 0],
  label = "",
  dualLabel = "",
  description = "",
  max = 100,
  min = 0,
  size = "responsive",
  onUpdate = dummyFn,
  onImmediateUpdate = dummyFn,
  step = 1,
  testId = "",
  sizeCalculationTrigger = 0,
  withInput = true,
  subLabels = ["", ""]
}) => {
  const sliderRef = useRef(null);
  const formatter = displayFormatter(format);
  const validInitialValue = valueInRange(initialValues[0], [min, max]);
  const validInitialSecondaryValue = valueInRange(initialValues[1] || 0, [
    validInitialValue,
    max
  ]);
  const [sliderSize, setSliderSize] = useState({ left: 0, width: 0 });
  const [values, setValues] = useState([
    validInitialValue,
    validInitialSecondaryValue
  ]);
  const gradient = useSliderGradient({
    min,
    max,
    values,
    dualRange,
    activeColor: activeColor || themeTokens.color.blue["2"],
    backgroundColor: backgroundColor || themeTokens.color.grey["2"]
  });
  useEffect(() => {
    setValues([validInitialValue, validInitialSecondaryValue]);
  }, [validInitialValue, validInitialSecondaryValue]);
  useEffect(() => {
    const updateSliderRef = () => {
      if (sliderRef && sliderRef.current) {
        const { width, left } = sliderRef.current.getBoundingClientRect();
        setSliderSize({ left, width });
      }
    };
    updateSliderRef();
    window.addEventListener("resize", updateSliderRef);
    return () => {
      window.removeEventListener("resize", updateSliderRef);
    };
  }, [sizeCalculationTrigger]);
  const validateAndSetValues = (newValues, shouldSignal) => {
    const safeValues = dualRange ? [
      valueInRange(Math.min(...newValues), [min, values[1]]),
      valueInRange(Math.max(...newValues), [values[0], max])
    ] : [
      valueInRange(newValues[0], [min, max]),
      valueInRange(newValues[1], [min, max])
    ];
    if (shouldSignal)
      onUpdate(safeValues);
    setValues(safeValues);
  };
  const updateValueFromTrackClick = (event) => {
    if (disabled)
      return;
    const { left, width } = sliderSize;
    const clickedValue = getValueFromPixels(min, max, event.clientX - left, width, step);
    const shouldUpdateSecondary = dualRange && valueIsCloserToSecond(clickedValue, values);
    const updatedValues = [
      shouldUpdateSecondary ? values[0] : clickedValue,
      shouldUpdateSecondary ? clickedValue : values[1]
    ];
    validateAndSetValues(updatedValues, true);
  };
  useEffect(() => {
    if (onImmediateUpdate)
      onImmediateUpdate(values);
  }, [values, onImmediateUpdate]);
  return /* @__PURE__ */ React.createElement(SliderContext.Provider, {
    value: {
      debounceDelay,
      disabled,
      errorMessage,
      formatter,
      max,
      min,
      sliderSize,
      step
    }
  }, /* @__PURE__ */ React.createElement(SliderOuterWrapper, {
    className: cx(className, disabled && "disabled", size === "small" && "slider--small", size === "medium" && "slider--medium")
  }, /* @__PURE__ */ React.createElement(SliderHeader, {
    sliderId: id,
    label,
    dualLabel,
    description,
    min,
    max,
    values,
    dualRange,
    validateAndSetValues,
    withInput
  }), /* @__PURE__ */ React.createElement("div", {
    ref: sliderRef,
    onClick: updateValueFromTrackClick,
    className: "slider__wrapper",
    role: "presentation"
  }, /* @__PURE__ */ React.createElement("div", {
    style: gradient,
    "data-testid": `${testId || id}--track`,
    className: "slider__track"
  }), /* @__PURE__ */ React.createElement(SliderHandle, {
    value: values[0],
    updateValue: (val = 0, update = false) => validateAndSetValues([val, values[1]], update),
    id,
    "data-testId": `${testId || id}--primaryHandle`,
    testId: `${testId || id}--primaryHandle`,
    className: "slider__primaryHandle"
  }), dualRange && /* @__PURE__ */ React.createElement(SliderHandle, {
    value: values[1],
    updateValue: (val = 0, update = false) => validateAndSetValues([values[0], val], update),
    id: `${id}--secondary`,
    "data-testId": `${testId || id}--secondaryHandle`,
    testId: `${testId || id}--secondaryHandle`,
    className: "slider__secondaryHandle"
  })), /* @__PURE__ */ React.createElement("div", {
    className: "slider__footer"
  }, /* @__PURE__ */ React.createElement("div", {
    "data-testid": `${id}--min-value`
  }, formatter(min)), /* @__PURE__ */ React.createElement("div", {
    "data-testid": `${id}--max-value`
  }, formatter(max))), subLabels.length && /* @__PURE__ */ React.createElement("div", {
    className: "slider__footer slider__sublabels"
  }, /* @__PURE__ */ React.createElement("div", null, subLabels[0]), /* @__PURE__ */ React.createElement("div", null, subLabels[1]))));
};
export default Slider;
